import React, { FC, useReducer, createContext, useContext } from 'react';

export enum ACTIONS {
  SEARCH_CUSTOMER,
  SWITCH_CUSTOMER_TYPE,
  CLEAR,
}
interface CustomerSearchValues {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  birthDate: Date | null;
  identityDocumentSerialCode: string;
  identityDocumentSerialNumber: string;
}
interface LoanApplicationState {
  loanApplication: string;
  customerId?: string;
  customerNumber?: string;
  customerSearchValues?: CustomerSearchValues;
  isNewCustomer: boolean;
  pageNumber?: number;
}
type Action = { type: ACTIONS; payload?: any };
const loanApplicationReducer = (
  state: LoanApplicationState,
  action: Action
): LoanApplicationState => {
  switch (action.type) {
    case ACTIONS.SEARCH_CUSTOMER:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.SWITCH_CUSTOMER_TYPE:
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
};
type LoanApplicationDispatch = (action: Action) => void;
const LoanApplicationStateContext = createContext<LoanApplicationState | undefined>(undefined);
const LoanApplicationDispatchContext = createContext<LoanApplicationDispatch | undefined>(
  undefined
);

const initialValues = { loanApplication: '', isNewCustomer: false };

const LoanApplicationProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(loanApplicationReducer, initialValues);

  return (
    <LoanApplicationStateContext.Provider value={state}>
      <LoanApplicationDispatchContext.Provider value={dispatch}>
        {children}
      </LoanApplicationDispatchContext.Provider>
    </LoanApplicationStateContext.Provider>
  );
};
type SearchInput = Pick<
  LoanApplicationState,
  'customerId' | 'customerNumber' | 'customerSearchValues' | 'isNewCustomer' | 'pageNumber'
>;
export const useCustomerSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(LoanApplicationDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCustomerSearchDispatch must be used within a LoanApplicationProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_CUSTOMER, payload });
};
export const useCustomerSearchState = (): LoanApplicationState => {
  const context = useContext(LoanApplicationStateContext);
  if (context === undefined) {
    throw new Error('useCustomerSearchState must be used within a LoanApplicationProvider');
  }

  return context;
};
export const useClearDispatch = (isOnlyCustomerSearch = false): (() => void) => {
  const dispatch = useContext(LoanApplicationDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a LoanApplicationProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR, payload: { isOnlyCustomerSearch } });
};
export default LoanApplicationProvider;
